import React, { useState, useEffect, useMemo } from "react";
import { Modal, Box, Button, Typography, Tabs, Tab, Grid, Card, CardContent, Divider, Tooltip } from "@mui/material";
import DataTable from "../DataTable/DataTable";
import { IoClose } from "react-icons/io5";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ height: "100%", overflow: "auto" }}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SofDetail = ({ open, setOpen, data, invoiceNo }) => {
  const [tabValue, setTabValue] = useState(0);

  // Function to open the modal
  const handleOpen = () => setOpen(true);

  // Function to close the modal
  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (open) {
      setTabValue(0);
    }
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Safe extraction of data
  // Handle backward compatibility where data might be just an array of items
  const { items, parties, signatures } = Array.isArray(data) 
    ? { items: data, parties: [], signatures: [] } 
    : { items: data?.items || [], parties: data?.parties || [], signatures: data?.signatures || [] };

  // Filtered item lists (all comparisons use lowercase)
  const validServices = useMemo(() => {
    return items.filter((item) => {
      const activity = (item.activity || "").toString().toLowerCase().trim();
      return activity !== "removed" && activity !== "";
    });
  }, [items]);

  const removedServices = useMemo(() => {
    return items.filter((item) => {
      const activity = (item.activity || "").toString().toLowerCase().trim();
      return activity === "removed";
    });
  }, [items]);

  const appendixItems = useMemo(() => {
    return items.filter((item) => {
      const rowFlag = (item.row_flag || "").toString().toLowerCase().trim();
      return rowFlag === "appendix_item";
    });
  }, [items]);

  const pipItems = useMemo(() => {
    return items.filter((item) => {
      const rowFlag = (item.row_flag || "").toString().toLowerCase().trim();
      return rowFlag === "pip_item";
    });
  }, [items]);

  const columns = [
    { field: "service_id", headerName: "Service ID", width: 150 },
    { field: "service_name", headerName: "Service Name", width: 150 },
    {
      field: "service_commitment",
      headerName: "Service Commitment",
      width: 150,
    },
    {
      field: "service_description",
      headerName: "Service Description",
      width: 200,
    },
    { field: "quantity", headerName: "Quantity", width: 150 },
    { field: "currency", headerName: "Currency", width: 150 },
    { 
      field: "conversion_rate", 
      headerName: "Conversion Rate", 
      width: 150,
      renderCell: (params) => {
        const val = params.value;
        if (val === null || val === undefined || val === "N/A" || val === "") return "N/A";
        const num = Number(val);
        if (isNaN(num)) return val;
        const formatted = parseFloat(num.toFixed(3)).toString();
        return (
          <Tooltip title={String(val)} arrow placement="top">
            <span style={{ cursor: "pointer", borderBottom: "1px dashed #7C2EA0" }}>
              {formatted}
            </span>
          </Tooltip>
        );
      }
    },
    { field: "mrc", headerName: "MRC", width: 150 },
    { field: "converted_mrc_usd", headerName: "MRC (USD)", width: 150 },
    { field: "nrc", headerName: "NRC", width: 150 },
    { field: "converted_nrc_usd", headerName: "NRC (USD)", width: 150 },
    { field: "location_id", headerName: "Location ID", width: 150 },
    {
      field: "activity",
      headerName: "Activity",
      width: 200,
    },
  ];

  const partiesColumns = [
    { field: "name", headerName: "Name", width: 250 },
    { field: "entity_type", headerName: "Entity Type", width: 150 },
    { field: "role", headerName: "Role", width: 150 },
    { field: "address", headerName: "Address", width: 300 },
    { field: "tax_number", headerName: "Tax Number", width: 150 },
    { field: "ref_id", headerName: "Ref ID", width: 100 },
  ];

  const handleDownloadCSV = () => {
    const csvRows = [];
    const headers = [
      "Service ID",
      "Service Name",
      "Service Commitment",
      "Service Description",
      "Quantity",
      "Currency",
      "Conversion Rate",
      "MRC",
      "MRC (USD)",
      "NRC",
      "NRC (USD)",
      "Location ID",
      "Activity",
    ];
    csvRows.push(headers.map((header) => `"${header}"`).join(",")); // Quote headers

    items?.forEach((invoice) => {
      const row = [
        invoice?.service_id || "N/A",
        invoice?.service_name || "N/A",
        invoice?.service_commitment || "N/A",
        invoice?.service_description
          ? invoice.service_description.replace(/\n/g, " ")
          : "N/A",
        invoice?.quantity || "N/A",
        invoice?.currency || "N/A",
        invoice?.conversion_rate || "N/A",
        invoice?.mrc || "N/A",
        invoice?.converted_mrc_usd || "N/A",
        invoice?.nrc || "N/A",
        invoice?.converted_nrc_usd || "N/A",
        invoice?.location_id || "N/A",
        invoice?.activity || "N/A",
      ].map((value) => `"\t${String(value).replace(/"/g, '""')}"`);

      csvRows.push(row.join(",")); // Join row values correctly
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `SOF_Details_${invoiceNo}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            width: "90%",
            height: "80%",
            maxWidth: "1200px",
            borderRadius: 2,
            overflow: "hidden", // Changed to hidden to let TabPanel scroll
            display: "flex",
            flexDirection: "column",
            zIndex: 99999,
          }}
        >
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <Typography
                id="modal-title"
                variant="h6"
                component="h2"
                sx={{ fontSize: "24px" }}
              >
                SOF Details
              </Typography>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "20px",
                }}
              >
                <button
                  onClick={handleDownloadCSV}
                  style={{
                    background: "#4CAF50",
                    color: "#fff",
                    padding: "10px",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Download CSV
                </button>
                <IoClose
                  size={24}
                  onClick={() => {
                    setOpen(false);
                  }}
                  style={{ cursor: "pointer" }}
                />
              </div>
            </div>

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="SOF details tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="All Services" />
                <Tab label="Valid Services" />
                <Tab label="Removed Services" />
                <Tab label="Appendix" />
                <Tab label="PIP Items" />
                <Tab label="Parties" />
                <Tab label="Signatures" />
              </Tabs>
            </Box>

            {/* Tab 0: All Services */}
            <TabPanel value={tabValue} index={0}>
              <DataTable columns={columns} rows={items} />
            </TabPanel>

            {/* Tab 1: Valid Services — activity is not "removed" and not blank */}
            <TabPanel value={tabValue} index={1}>
              {validServices.length > 0 ? (
                <DataTable columns={columns} rows={validServices} />
              ) : (
                <Typography>No valid services available.</Typography>
              )}
            </TabPanel>

            {/* Tab 2: Removed Services — activity is "removed" */}
            <TabPanel value={tabValue} index={2}>
              {removedServices.length > 0 ? (
                <DataTable columns={columns} rows={removedServices} />
              ) : (
                <Typography>No removed services available.</Typography>
              )}
            </TabPanel>

            {/* Tab 3: Appendix — row_flag is "appendix_item" */}
            <TabPanel value={tabValue} index={3}>
              {appendixItems.length > 0 ? (
                <DataTable columns={columns} rows={appendixItems} />
              ) : (
                <Typography>No appendix items available.</Typography>
              )}
            </TabPanel>

            {/* Tab 4: PIP Items — row_flag is "pip_item" */}
            <TabPanel value={tabValue} index={4}>
              {pipItems.length > 0 ? (
                <DataTable columns={columns} rows={pipItems} />
              ) : (
                <Typography>No PIP items available.</Typography>
              )}
            </TabPanel>

            {/* Tab 5: Parties */}
            <TabPanel value={tabValue} index={5}>
              {parties.length > 0 ? (
                 <DataTable 
                 columns={partiesColumns} 
                 rows={parties.map((p, i) => ({...p, id: i, role: p.entity_type}))} 
               />
              ) : (
                <Typography>No parties data available.</Typography>
              )}
            </TabPanel>

            {/* Tab 6: Signatures */}
            <TabPanel value={tabValue} index={6}>
              {signatures.length > 0 ? (
                <Grid container spacing={3}>
                  {signatures.map((sig, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {sig.party || "Signature"}
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Typography variant="body2"><strong>Name:</strong> {sig.name || "N/A"}</Typography>
                          <Typography variant="body2"><strong>Title/Position:</strong> {sig.title || sig.position || "N/A"}</Typography>
                          <Typography variant="body2"><strong>Email:</strong> {sig.email || "N/A"}</Typography>
                          <Typography variant="body2"><strong>Date:</strong> {sig.date || "N/A"}</Typography>
                          <Typography variant="body2"><strong>Status:</strong> {sig.status || "N/A"}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography>No signatures data available.</Typography>
              )}
            </TabPanel>
          </>
        </Box>
      </Modal>
    </div>
  );
};

export default SofDetail;
